import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, Truck, X } from 'lucide-react';
import { useTranslation } from '@sshop/i18n';
import { useCart } from '@/features/cart';
import { useShippingOptions } from '@/features/checkout';
import { useLocale } from '@/shared/hooks/useLocale';
import { deliveryCityStorageKey } from '@/shared/lib/deliveryCityStorage';
import { deliveryProgress, freeDeliveryOption } from '@/shared/lib/deliveryPricing';
import { routes } from '@/shared/lib/routes';

export function FreeDeliveryNotice() {
  const { t } = useTranslation('checkout');
  const locale = useLocale();
  const { data: cart } = useCart();
  const { data: options } = useShippingOptions();
  const [openedAt, setOpenedAt] = useState(0);
  const [cityId, setCityId] = useState('');
  useEffect(() => {
    const show = () => {
      try { setCityId(localStorage.getItem(deliveryCityStorageKey) ?? ''); } catch { setCityId(''); }
      setOpenedAt(Date.now());
    };
    window.addEventListener('sshop:cart-added', show);
    return () => window.removeEventListener('sshop:cart-added', show);
  }, []);
  useEffect(() => {
    if (!openedAt) return;
    const timer = window.setTimeout(() => setOpenedAt(0), 12000);
    return () => window.clearTimeout(timer);
  }, [openedAt]);

  const selected = options?.find(option => option.id === cityId);
  const option = freeDeliveryOption(options, selected);
  if (!openedAt || !cart || cart.itemCount === 0 || !option?.noticeEnabled ||
    option.freeShippingThreshold == null || option.price.amount === 0) return null;
  const progress = deliveryProgress(cart, option.freeShippingThreshold);
  const amount = (value: number) => new Intl.NumberFormat(locale === 'ge' ? 'ka-GE' : 'en-US', { maximumFractionDigits: 2 }).format(value);

  return (
    <div className="z-toast fixed inset-x-3 top-3 mx-auto max-w-xl rounded-2xl border border-emerald-200 bg-white p-4 text-slate-900 shadow-xl sm:top-5 sm:p-5" role="status" aria-live="polite" aria-atomic="true">
      <div className="flex items-start gap-3">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-emerald-50 text-emerald-700">
          {progress.remaining === 0 ? <Check size={21} /> : <Truck size={21} />}
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-bold">{t(progress.remaining === 0 ? 'freeDeliveryUnlocked' : 'freeDeliveryProgress', { remaining: amount(progress.remaining) })}</p>
          <p className="mt-1 text-xs text-slate-500">{t('freeDeliveryCartTotal', { total: amount(progress.total) })} · {selected ? selected.label : t('freeDeliveryEligibleCities')}</p>
          <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-slate-100" aria-hidden="true">
            <div className="h-full rounded-full bg-emerald-500 transition-all duration-500" style={{ width: `${progress.percent}%` }} />
          </div>
          <Link className="mt-2 inline-block text-xs font-semibold text-emerald-700 underline underline-offset-2" to={routes.cart(locale)} onClick={() => setOpenedAt(0)}>{t('freeDeliveryViewCart')}</Link>
        </div>
        <button type="button" className="-m-1 rounded-lg p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-700" onClick={() => setOpenedAt(0)} aria-label={t('dismissDeliveryNotice')}><X size={17} /></button>
      </div>
    </div>
  );
}
